// React import removed
import { Link } from 'react-router-dom'
import {
  TrophyIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  CalendarIcon,
  MapPinIcon,
  ClockIcon,

  ArrowRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

const PublicHome: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <TrophyIcon className="mx-auto h-16 w-16 mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              St. Mary's Coptic Orthodox Church
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold mb-6">
              Annual Mahragan Competition
            </h2>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              Join churches across the region in celebrating faith, talent, and community through
              spiritual competitions, arts, and sports
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register/participant"
                className="inline-flex items-center px-8 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                <UserGroupIcon className="h-5 w-5 mr-2" />
                Register as Participant
                <ArrowRightIcon className="h-4 w-4 ml-2" />
              </Link>
              <Link
                to="/register/church"
                className="inline-flex items-center px-8 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-400 transition-colors border-2 border-white"
              >
                <BuildingOfficeIcon className="h-5 w-5 mr-2" />
                Register Your Church
                <ArrowRightIcon className="h-4 w-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Event Information */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Event Information</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Our annual Mahragan brings together churches from across the region for a celebration of faith,
            talent, and community spirit.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="text-center">
            <CalendarIcon className="mx-auto h-12 w-12 text-blue-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">When</h3>
            <p className="text-gray-600">
              Summer 2025<br />
              <span className="text-sm">Exact dates to be announced</span>
            </p>
          </div>
          <div className="text-center">
            <MapPinIcon className="mx-auto h-12 w-12 text-blue-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Where</h3>
            <p className="text-gray-600">
              Host church location<br />
              <span className="text-sm">Details sent to registered participants</span>
            </p>
          </div>
          <div className="text-center">
            <ClockIcon className="mx-auto h-12 w-12 text-blue-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Duration</h3>
            <p className="text-gray-600">
              3-day weekend event<br />
              <span className="text-sm">Friday evening through Sunday</span>
            </p>
          </div>
        </div>
      </div>

      {/* Competition Categories */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Competition Categories</h2>
            <p className="text-lg text-gray-600">
              Compete in various categories designed to celebrate faith, talent, and teamwork
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Coptic Hymns',
                description: 'Traditional liturgical chanting',
                icon: '🎵',
                ageGroups: 'Ages 6+'
              },
              {
                title: 'Spiritual Knowledge',
                description: 'Biblical and church history',
                icon: '📖',
                ageGroups: 'Ages 6+'
              },
              {
                title: 'Coptic Language',
                description: 'Reading and understanding',
                icon: '📝',
                ageGroups: 'Ages 9+'
              },
              {
                title: 'Art & Creativity',
                description: 'Drawing and creative arts',
                icon: '🎨',
                ageGroups: 'Ages 6+'
              },
              {
                title: 'Soccer',
                description: 'Team soccer competition',
                icon: '⚽',
                ageGroups: 'Ages 9+'
              },
              {
                title: 'Volleyball',
                description: 'Team volleyball games',
                icon: '🏐',
                ageGroups: 'Ages 12+'
              },
              {
                title: 'Chess',
                description: 'Individual chess tournament',
                icon: '♟️',
                ageGroups: 'Ages 9+'
              },
              {
                title: 'Table Tennis',
                description: 'Individual ping pong matches',
                icon: '🏓',
                ageGroups: 'Ages 9+'
              }
            ].map((category, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 text-center">
                <div className="text-4xl mb-3">{category.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{category.title}</h3>
                <p className="text-gray-600 mb-2">{category.description}</p>
                <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  {category.ageGroups}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Age Groups */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Age Groups</h2>
            <p className="text-lg text-gray-600">
              Competitions are organized by age groups to ensure fair and appropriate participation
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { group: '6-8 years', description: 'Early childhood' },
              { group: '9-11 years', description: 'Elementary' },
              { group: '12-14 years', description: 'Middle school' },
              { group: '15-17 years', description: 'High school' },
              { group: '18+ years', description: 'Adults' }
            ].map((age, index) => (
              <div key={index} className="bg-white rounded-lg shadow p-4 text-center">
                <h3 className="text-lg font-semibold text-blue-600 mb-1">{age.group}</h3>
                <p className="text-sm text-gray-600">{age.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Registration Process */}
      <div className="bg-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How to Register</h2>
            <p className="text-lg text-gray-600">
              Simple steps to join the Mahragan competition
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Church Registration Process */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="text-center mb-6">
                <BuildingOfficeIcon className="mx-auto h-12 w-12 text-blue-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900">Church Registration</h3>
                <p className="text-gray-600 mt-2">For church coordinators and leaders</p>
              </div>

              <div className="space-y-4">
                {[
                  'Complete church information form',
                  'Provide contact and coordinator details',
                  'Upload supporting documents (optional)',
                  'Submit for admin approval',
                  'Receive confirmation and next steps'
                ].map((step, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                      {index + 1}
                    </div>
                    <p className="text-gray-700">{step}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link
                  to="/register/church"
                  className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  <BuildingOfficeIcon className="h-5 w-5 mr-2" />
                  Register Your Church
                </Link>
              </div>
            </div>

            {/* Participant Registration Process */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="text-center mb-6">
                <UserGroupIcon className="mx-auto h-12 w-12 text-green-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900">Participant Registration</h3>
                <p className="text-gray-600 mt-2">For individual participants</p>
              </div>

              <div className="space-y-4">
                {[
                  'Fill out personal information',
                  'Provide contact and medical details',
                  'Select age-appropriate competitions',
                  'Submit for church coordinator approval',
                  'Receive schedule and event details'
                ].map((step, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                      {index + 1}
                    </div>
                    <p className="text-gray-700">{step}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link
                  to="/register/participant"
                  className="w-full flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
                >
                  <UserGroupIcon className="h-5 w-5 mr-2" />
                  Register as Participant
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Important Information */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Important Information</h2>

            <div className="space-y-6">
              <div className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Registration Deadline</h3>
                  <p className="text-gray-600">
                    All registrations must be completed by the announced deadline. Late registrations may not be accepted.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Church Approval Required</h3>
                  <p className="text-gray-600">
                    Churches must be registered and approved before participants from that church can register.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Age Verification</h3>
                  <p className="text-gray-600">
                    Participants may be required to provide age verification documents during the event.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Code of Conduct</h3>
                  <p className="text-gray-600">
                    All participants must adhere to the Mahragan code of conduct emphasizing Christian values and sportsmanship.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Event Updates</h3>
                  <p className="text-gray-600">
                    Registered participants will receive email updates about schedules, location details, and any changes to the event.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <TrophyIcon className="mx-auto h-12 w-12 mb-4" />
            <h3 className="text-xl font-semibold mb-2">St. Mary's Coptic Orthodox Church Mahragan</h3>
            <p className="text-gray-400 mb-6">
              Celebrating faith, talent, and community through spiritual and athletic competition
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link
                to="/register/participant"
                className="inline-flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Register as Participant
              </Link>
              <Link
                to="/register/church"
                className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Register Your Church
              </Link>
            </div>

            <p className="text-sm text-gray-500">
              For questions about registration or the event, please contact your church coordinator or the organizing committee.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default PublicHome